import { useMutation } from '@tanstack/react-query'
import { t } from 'i18next'
import { DocumentUpload } from '@node-space/storybook-components/dist/UploadFile'
import { useAccountPreferenceMutation } from 'hooks/mutations/useAccountPreferenceMutation'
import { useAccountPreferencesQuery } from 'hooks/queries/useAccountPreferencesQuery'
import { useToastContext } from 'hooks/useToastContext'
import { postBusinessDocuments } from 'services/VerificationService'
import { SelectedSignatory } from 'types/accounts'
import { BusinessDocument, BusinessDocumentsPayload } from 'types/verification'
import { cleanBase64String } from 'utils/utils'

interface UseUploadDocumentProps {
  onSuccess?: () => void
  onError?: (error: any) => void
  selectedSignatory: SelectedSignatory | null
}

interface UseUploadDocumentResult {
  uploadDocument: (formDocument: DocumentUpload) => void
  isUpdatingPreference: boolean
  isDocumentLoading: boolean
}

export const useUploadDocument = ({
  onSuccess,
  onError,
  selectedSignatory,
}: UseUploadDocumentProps): UseUploadDocumentResult => {
  const { refetch: refetchPreferences } = useAccountPreferencesQuery()
  const addToast = useToastContext()
  const { mutate: updateSignatoryPreference, isPending: isUpdatingPreference } =
    useAccountPreferenceMutation<SelectedSignatory>()

  const { mutate, isPending: isDocumentLoading } = useMutation({
    mutationFn: async (formDocument: DocumentUpload) => {
      const accountDocuments: BusinessDocument[] = [
        {
          code: 'additionalDocument',
          file: {
            name: 'Power of attorney',
            base64: cleanBase64String(formDocument?.buffer as string),
          },
        },
      ]

      const payloadData: BusinessDocumentsPayload = {
        accountDocuments: accountDocuments,
      }

      return postBusinessDocuments(payloadData)
    },
    onSuccess: async () => {
      addToast({ title: t('verification.documentSubmitted'), state: 'success' })

      if (selectedSignatory) {
        updateSignatoryPreference(
          { preferenceKey: 'selectedSignatory', value: selectedSignatory },
          {
            onSuccess: () => {
              refetchPreferences()
              !!onSuccess && onSuccess()
            },
            onError: error => {
              !!onError && onError(error)
            },
          }
        )
      } else {
        refetchPreferences()
        !!onSuccess && onSuccess()
      }
    },
    onError: error => {
      !!onError && onError(error)
    },
  })

  const uploadDocument = async (formDocument: DocumentUpload) => {
    mutate(formDocument)
  }

  return {
    uploadDocument,
    isUpdatingPreference,
    isDocumentLoading,
  }
}
